/**
 * In-memory Ask SB conversation for the current browser tab.
 * Keyed by uid + tenantId. Not written to localStorage, sessionStorage, or IndexedDB.
 * Survives component unmount. Cleared on sign-out, tenant change, New conversation,
 * and an ask_sb_revision_mismatch.
 */

const buckets = new Map();
let activeIdentity = '';

export function askSbConversationKey(uid, tenantId) {
  return `${String(uid || '')}\u0000${String(tenantId || '')}`;
}

function emptyConversation() {
  return { revision: null, turns: [], messages: [] };
}

function copyConversation(value) {
  const source = value || emptyConversation();
  return {
    revision: source.revision ?? null,
    turns: (source.turns || []).map((turn) => ({ ...turn })),
    messages: (source.messages || []).map((message) => ({
      ...message,
      sources: Array.isArray(message.sources) ? message.sources.map((source) => ({ ...source })) : undefined,
    })),
  };
}

export function readAskSbConversation(uid, tenantId) {
  if (!uid || !tenantId) return emptyConversation();
  return copyConversation(buckets.get(askSbConversationKey(uid, tenantId)));
}

export function writeAskSbConversation(uid, tenantId, conversation) {
  if (!uid || !tenantId) return;
  buckets.set(askSbConversationKey(uid, tenantId), copyConversation(conversation));
}

export function clearAskSbConversation(uid, tenantId) {
  if (!uid || !tenantId) return;
  buckets.delete(askSbConversationKey(uid, tenantId));
}

export function clearAllAskSbConversations() {
  buckets.clear();
  activeIdentity = '';
}

/**
 * Track the signed-in user and tenant. A change drops every stored conversation.
 * An empty uid is sign-out. A missing tenantId waits until the tenant is known.
 * @returns {'signed-out'|'pending'|'tenant-changed'|'ready'}
 */
export function syncAskSbIdentity(uid, tenantId) {
  if (!uid) {
    clearAllAskSbConversations();
    return 'signed-out';
  }
  if (!tenantId) return 'pending';
  const next = askSbConversationKey(uid, tenantId);
  if (activeIdentity && activeIdentity !== next) {
    buckets.clear();
    activeIdentity = next;
    return 'tenant-changed';
  }
  activeIdentity = next;
  return 'ready';
}

/**
 * Drop the conversation when the server revision no longer matches what we stored.
 * @returns {boolean} true when memory was cleared
 */
export function clearAskSbOnRevisionMismatch(uid, tenantId, revision) {
  const current = readAskSbConversation(uid, tenantId);
  if (current.revision == null || revision == null) return false;
  if (Number(current.revision) !== Number(revision)) {
    clearAskSbConversation(uid, tenantId);
    return true;
  }
  return false;
}
